import React from 'react';
import { useLocation, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedText } from '../helpers/resolveLocalizedText';
import { Page } from '../components/__design-system/layout/page';
import { AnimatedImageGallery } from '../components/animated-image-gallery/AnimatedImageGallery';
import { BookingOptionTile } from '../components/booking-options';
import { RightArrowIcon } from '../components/booking-options/platform/BookWithTablz';
import { LanguageSelector } from '../components/language-selector/LanguageSelector';
import { getPatioSection } from '../helpers/getPatioSection';
import { mostCompatibleGroupSize } from '../helpers/groupSIze';
import { RestaurantGuardOutletContext } from '../routes/RestaurantGuard';
import styles from './LandingPage.module.scss';
import { analytics, NAVIGATE_TO_PARAMS } from '../helpers/analytics';
import { useScreenSize } from '../hooks/useScreenSize';
import { getExploreEntryPath } from '../helpers/open-parameters/getExploreEntryPath';
export const LandingPageSeatingOptions = () => {
	const { i18n } = useTranslation();
	const location = useLocation();
	const { restaurant, tables, featureFlags } = useOutletContext<RestaurantGuardOutletContext>();
	const { isSmallScreen } = useScreenSize();

	const patioSection = getPatioSection(tables);

	const groupSize = mostCompatibleGroupSize(tables);
	const searchParams = new URLSearchParams(location.search);
	if (featureFlags?.autoSelectGroupSize) {
		searchParams.set('groupSize', String(groupSize));
	}
	const search = searchParams.toString() ? `?${searchParams.toString()}` : '';
	const findTableLink = `/${restaurant.slug}/${getExploreEntryPath(isSmallScreen)}${search}`;

	let patioLink = '';
	if (patioSection) {
		patioLink = `/${restaurant.slug}/explore/${patioSection.slug}${location.search}`;
	}
	if (!!restaurant.settings?.alternativeBookingOption?.label) {
		const altParams = new URLSearchParams(location.search);
		altParams.set('skip', 'true');
		patioLink = `/${restaurant?.settings?.alternativeBookingOption?.href}?${altParams.toString()}`;
	}

	const reverseOrder = restaurant?.settings?.alternativeBookingOption?.selfPosition === 'right';
	const defaultOrder = !reverseOrder;

	const primaryOption = <BookingOptionTile
		title={resolveLocalizedText(restaurant.settings?.alternativeBookingOption?.selfLabel, i18n.language) || 'Sit Inside...'}
		backgroundImage={<AnimatedImageGallery restaurant={restaurant} tables={tables} staticImage />}
		icon={<RightArrowIcon />}
		linkTo={findTableLink}
		onClick={() => {
			analytics.track(NAVIGATE_TO_PARAMS, {
				from: 'Landing Page Seating Options',
				groupSize: featureFlags?.autoSelectGroupSize ? groupSize : null,
				autoselectGroupSize: featureFlags?.autoSelectGroupSize
			});
		}}
		ariaLabel={resolveLocalizedText(restaurant.settings?.alternativeBookingOption?.selfLabel, i18n.language) || 'Sit Inside'}
		flex={1}
		justifyStart
	/>;


	return (
		<Page as='main'>
			<LanguageSelector />
			<div className={styles.Options}>
				{defaultOrder ? primaryOption : null}

				<BookingOptionTile
					title={resolveLocalizedText(restaurant.settings?.alternativeBookingOption?.label, i18n.language) || 'Sit Outside...'}
					backgroundImage={<AnimatedImageGallery restaurant={restaurant} tables={tables} staticPatioImage />}
					icon={<RightArrowIcon />}
					linkTo={patioLink}
					ariaLabel={resolveLocalizedText(restaurant.settings?.alternativeBookingOption?.label, i18n.language) || 'Sit Outside'}
					flex={1}
					justifyStart
				/>

				{reverseOrder ? primaryOption : null}
			</div>
		</Page>
	);
};
